<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { XMarkIcon } from '@heroicons/vue/20/solid'
import { FleetingLabelDuration } from '@/Support/Global/Interfaces/FleetingLabelDuration'
import { getFleetingTime } from '@/Support/Common/Functions/FleetingTime'

const props = defineProps<{
  title: string
  context?: string | null
  buttonLabel?: string
  fleeting?: boolean
  duration?: FleetingLabelDuration
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'click'): void
}>()

const show = ref(true)

if (props.fleeting) {
  setTimeout(() => close(), getFleetingTime(props.duration))
}

const close = () => {
  show.value = false
  setTimeout(() => emit('close'), 350)
}
</script>

<template>
  <!-- Global notification live region, render this permanently at the end of the document -->
  <div
    aria-live="assertive"
    class="pointer-events-none flex items-end px-4 py-6 sm:items-start sm:p-6"
  >
    <div class="flex w-full flex-col items-center space-y-4 sm:items-end">
      <!-- Notification panel, dynamically insert this into the live region when it needs to be displayed -->
      <transition
        enter-active-class="transform ease-out duration-300 transition"
        enter-from-class="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
        enter-to-class="translate-y-0 opacity-100 sm:translate-x-0"
        leave-active-class="transition ease-in duration-300"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="show"
          class="pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg bg-linear-to-br from-accent-500 to-accent-600 dark:from-accent-800 dark:to-accent-900 opacity-90 shadow-lg ring-1 ring-black/5"
        >
          <div class="p-4">
            <div class="flex items-center">
              <div class="flex w-0 flex-1 justify-between items-baseline">
                <span class="grow-0 text-sm font-medium text-neutral-50">
                  {{ __(title) }}
                </span>
                <span
                  v-if="context"
                  class="ml-1 flex-1 text-neutral-200 truncate text-xs"
                >
                  {{ __(context) }}
                </span>
                <button
                  v-if="buttonLabel"
                  type="button"
                  class="ml-3 shrink-0 rounded-md text-sm font-medium text-primary-100 hover:text-primary-50 focus:outline-hidden focus:ring-1 focus:ring-primary-100 focus:ring-offset-2"
                  @click="emit('click')"
                >
                  {{ __(buttonLabel) }}
                </button>
              </div>
              <div class="ml-4 flex shrink-0">
                <button
                  type="button"
                  @click="close"
                  class="inline-flex rounded-md text-danger-100 hover:text-neutral-50 focus:outline-hidden focus:ring-1 focus:ring-danger-100 focus:ring-offset-2"
                >
                  <span class="sr-only">
                    {{ __('Close') }}
                  </span>
                  <XMarkIcon class="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>
